import React from 'react';
import {
  Facebook,
  Linkedin,
  Instagram,
  Apple,
  PlayCircle,
  ArrowRight,
  HelpCircle,
  ExternalLink,
  MapPin,
  Phone,
  Mail
} from 'lucide-react';
import { FaGooglePlay, FaApple } from 'react-icons/fa';
import FAQ from './FAQ';
import { useLocation } from 'react-router-dom';

const Footer = () => {
  const location = useLocation();

  // Newsletter state
  const [newsletterEmail, setNewsletterEmail] = React.useState("");
  const [newsletterLoading, setNewsletterLoading] = React.useState(false);
  const [newsletterMessage, setNewsletterMessage] = React.useState("");
  const [newsletterSuccess, setNewsletterSuccess] = React.useState(false);

  // Sendy list ID (replace with your actual list ID)
  const SENDY_LIST_ID = "8d6X2d9z8w9v8z9a8y7z8w9v8z9a8y7z"; // TODO: Replace with actual list ID

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    setNewsletterLoading(true);
    setNewsletterMessage("");
    setNewsletterSuccess(false);
    try {
      const payload = new URLSearchParams();
      payload.append("email", newsletterEmail);
      payload.append("list", SENDY_LIST_ID);
      payload.append("subform", "yes");
      // Optionally add name or other fields here if needed

      const response = await fetch("https://jama.co.in/sendy/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: payload.toString(),
      });
      const text = await response.text();
      // Debug log
      // console.log("Sendy response:", text);
      if (response.ok && /success|subscribed|check your email/i.test(text)) {
        setNewsletterSuccess(true);
        setNewsletterMessage("Thank you for subscribing! Please check your email to confirm.");
        setNewsletterEmail("");
      } else {
        setNewsletterSuccess(false);
        setNewsletterMessage(
          text.match(/already subscribed/i)
            ? "You are already subscribed."
            : text.replace(/<[^>]+>/g, "") || "Subscription failed. Please try again."
        );
      }
    } catch (error) {
      setNewsletterSuccess(false);
      setNewsletterMessage("An error occurred. Please try again later.");
    } finally {
      setNewsletterLoading(false);
    }
  }  

  return (
    <footer className="bg-[#113262] text-neutral-50">
      {/* Main Footer Content */}
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8">
        {location.pathname === '/' && (
          <>
            <FAQ />
            <div className="mb-12 border-b border-primary-300/20 pb-12" />
          </>
        )}
        {/* Download Section */}
        <div className="mb-12 border-b border-primary-300/20 pb-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-xl font-semibold mb-4 text-[#F49611]">Download App</h3>
              <p className="text-neutral-100 mb-6">Track your investments, manage goals, and stay updated on the go</p>
              <div className="flex flex-wrap gap-4">
                <a href="https://apps.apple.com/in/app/maxiom-wealth-advisory/id6443719443" className="flex items-center space-x-2 bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-all duration-300 shadow-lg hover:shadow-xl">
                  <FaApple className="w-6 h-6" />
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-semibold leading-tight">App Store</span>
                  </div>
                </a>
                <a href="https://play.google.com/store/apps/details?id=in.jama.app" className="flex items-center space-x-2 bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-all duration-300 shadow-lg hover:shadow-xl">
                  <FaGooglePlay className="w-5 h-5" />
                  <div className="flex flex-col items-start">
                    <span className="text-sm font-semibold leading-tight">Google Play</span>
                  </div>
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4 text-[#F49611]">Stay Updated</h3>
              <p className="text-neutral-100 mb-4">Subscribe to our newsletter for market insights and updates</p>
              <form
                action="https://jama.co.in/sendy/subscribe"
                method="POST"
                className="flex w-full"
              >
                <input type="hidden" name="list" value="dX7636SgpISFMiKelmkp0VTQ" />
                <input type="hidden" name="subform" value="yes" />
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="Enter your email"
                  autoComplete="email"
                  className="flex-1 px-4 py-3 bg-gray-500 text-white rounded-l-lg focus:outline-none focus:ring-2 focus:ring-primary-300 placeholder:text-neutral-400"
                />
                <button
                  type="submit"
                  className="bg-[#F49611] hover:bg-[#AB690C] px-6 py-3 rounded-r-lg transition-colors flex items-center justify-center"
                  aria-label="Subscribe"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Updated Contact Information Section */}
        <div className="mb-12 border-b border-primary-300/20 pb-12">
          <h3 className="text-xl font-semibold mb-6 text-[#F49611]">Contact Us</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Address */}
            <a
              href="https://www.google.co.in/maps/place/Maxiom+Wealth+-+SEBI+Regd+Investment+Advisors+%7C+Best+Financial+Advisory+Services+in+India/@17.433844,78.40398,19z/data=!4m14!1m7!3m6!1s0x3bcb9371cd3088c1:0x1e4353e8a6a1fb!2sMaxiom+Wealth+-+SEBI+Regd+Investment+Advisors+%7C+Best+Financial+Advisory+Services+in+India!8m2!3d17.433876!4d78.404275!16s%2Fg%2F11g_nfdb16!3m5!1s0x3bcb9371cd3088c1:0x1e4353e8a6a1fb!8m2!3d17.433876!4d78.404275!16s%2Fg%2F11g_nfdb16?entry=ttu&g_ep=EgoyMDI1MDMxNy4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary-900/30 p-6 rounded-lg flex group cursor-pointer hover:bg-primary-900/50 transition-colors"
            >
              <div className="mr-4">
                <div className="bg-[#F49611]/20 p-3 rounded-full group-hover:bg-[#F49611]/30 transition-colors">
                  <MapPin className="w-6 h-6 text-[#F49611]" />
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-2 text-[#F49611] transition-colors">Hyderabad Address</h4>
                <p className="text-neutral-100">
                  C2 (Awfis) 3rd Floor, Plot #800,<br />
                  Road #36, Jubilee Hills,<br />
                  Hyderabad, India - 500033
                </p>
              </div>
            </a>
            <a
              href="https://www.google.co.in/maps/place/BHIVE+Premium+Indiranagar/@12.9792148,77.6246697,15z/data=!3m1!4b1!4m6!3m5!1s0x3bae141e007a849d:0xbff26c814318fc77!8m2!3d12.9791945!4d77.6431238!16s%2Fg%2F11bxf3zpnf?coh=245189&entry=tts&g_ep=EgoyMDI1MDUxNS4wIPu8ASoJLDEwMjExNDU1SAFQAw%3D%3D&skid=b4643185-01db-42dd-8665-1c47c9b246e3"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary-900/30 p-6 rounded-lg flex group cursor-pointer hover:bg-primary-900/50 transition-colors"
            >
              <div className="mr-4">
                <div className="bg-[#F49611]/20 p-3 rounded-full group-hover:bg-[#F49611]/30 transition-colors">
                  <MapPin className="w-6 h-6 text-[#F49611]" />
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-2 text-[#F49611] transition-colors">Bengaluru Address</h4>
                <p className="text-neutral-100">
                  No. 467/468, BHIVE Workspace,<br />
                  Krishna Temple Road, Indiranagar 1" Stage,<br />
                  Bengaluru, Bengaluru Urban, Karnataka, 560038
                </p>
              </div>
            </a>
            {/* Phone
            <a
              href="tel:+919870264643"
              className="bg-primary-900/30 p-6 rounded-lg flex group cursor-pointer hover:bg-primary-900/50 transition-colors"
            >
              <div className="mr-4">
                <div className="bg-[#F49611]/20 p-3 rounded-full group-hover:bg-[#F49611]/30 transition-colors">
                  <Phone className="w-6 h-6 text-[#F49611]" />
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-2 group-hover:text-[#F49611] transition-colors">Call Us</h4>
                <p className="text-neutral-100">+91 98702 64643</p>
                <p className="mt-2 text-sm text-neutral-300">Mon-Sat 9:30 AM to 6:30 PM IST</p>
              </div>
            </a> */}

            {/* Email */}
            <div className="bg-primary-900/30 p-6 rounded-lg flex group cursor-pointer hover:bg-primary-900/50 transition-colors">
              <div className="mr-4">
                <a href="mailto:advisor@maxiomwealth.com" className="block">
                  <div className="bg-[#F49611]/20 p-3 rounded-full group-hover:bg-[#F49611]/30 transition-colors">
                    <Mail className="w-6 h-6 text-[#F49611]" />
                  </div>
                </a>
              </div>
              <div>
                <h4 className="font-semibold text-lg mb-2 text-[#F49611] transition-colors">Email</h4>
                <a
                  href="mailto:advisor@maxiomwealth.com"
                  className="text-neutral-100 hover:text-[#F49611] transition-colors"
                  onClick={(e) => {
                    // Attempt to open email client directly
                    window.location.href = "mailto:advisor@maxiomwealth.com";
                    e.stopPropagation();
                  }}
                >
                  advisor@maxiomwealth.com
                </a>
                <p className="mt-2 text-sm text-neutral-300">We will respond promptly</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Links Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Wealth Management */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#F49611]">Wealth Management Services</h3>
            <ul className="space-y-3">
              <li><a href="/wealth-services/portfolio-management" className="text-neutral-100 hover:text-[#F49611] transition-colors">Portfolio Management</a></li>
              <li><a href="/wealth-services/alternate-investments" className="text-neutral-100 hover:text-[#F49611] transition-colors">Alternate Investments</a></li>
              <li><a href="/wealth-services/estate-planning" className="text-neutral-100 hover:text-[#F49611] transition-colors">Estate Planning</a></li>
              <li><a href="/wealth-services/offshore-products" className="text-neutral-100 hover:text-[#F49611] transition-colors">Offshore Products</a></li>
              <li><a href="/wealth-services/private-credit" className="text-neutral-100 hover:text-[#F49611] transition-colors">Private Credit</a></li>
              <li><a href="/wealth-services/tax-planning" className="text-neutral-100 hover:text-[#F49611] transition-colors">Tax Planning</a></li>
            </ul>
          </div>
          {/* Financial Planning */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#F49611]">Financial Advisory Services</h3>
            <ul className="space-y-3">
              <li><a href="/financial-advisory/financial-planning" className="text-neutral-100 hover:text-[#F49611] transition-colors">Financial Planning</a></li>
              <li><a href="/financial-advisory/retirement-planning" className="text-neutral-100 hover:text-[#F49611] transition-colors">Retirement Planning</a></li>
              <li><a href="/services/finanacial-wellness-solutions" className="text-neutral-100 hover:text-[#F49611] transition-colors">Corporate Financial Wellness</a></li>
              <li><a href="/financial-advisory/goal-based-investments" className="text-neutral-100 hover:text-[#F49611] transition-colors">Goal-Based Planning</a></li>
            </ul>
          </div>

          {/* Mutual Funds
          <div>
            <h3 className="text-lg font-semibold mb-4">Mutual Fund Products</h3>
            <ul className="space-y-3">
              <li><a href="/mutual-funds-pricing" className="text-neutral-100 hover:text-[#F49611] transition-colors">Equity Funds</a></li>
              <li><a href="/mutual-funds-pricing" className="text-neutral-100 hover:text-[#F49611] transition-colors">Debt Funds</a></li>
              <li><a href="/mutual-funds-pricing" className="text-neutral-100 hover:text-[#F49611] transition-colors">Hybrid Funds</a></li>
              <li><a href="/mutual-funds-pricing" className="text-neutral-100 hover:text-[#F49611] transition-colors">Index Funds</a></li>
              <li><a href="/mutual-funds-pricing" className="text-neutral-100 hover:text-[#F49611] transition-colors">Most Purchased Funds</a></li>
            </ul>
          </div> */}

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#F49611]">Quick Links</h3>
            <ul className="space-y-3">
            <li><a href="/careers" className="text-neutral-100 hover:text-[#F49611] transition-colors">Careers</a></li>
              <li><a href="/contact-us" className="text-neutral-100 hover:text-[#F49611] transition-colors">Contact Us</a></li>
              <li><a href="/fee" className="text-neutral-100 hover:text-[#F49611] transition-colors">Pricing</a></li>
              <li><a href="/investor-center" className="text-neutral-100 hover:text-[#F49611] transition-colors">Investor Center</a></li>
            </ul>
          </div>
        </div>
        {/* 2nd Main Links Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Portfolio Management */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#F49611] whitespace-nowrap">Portfolio Management Services</h3>
            <ul className="space-y-3">
              <li><a href="/jewel-pms-large-midcap-focused" className="text-neutral-100 hover:text-[#F49611] transition-colors">JEWEL</a></li>
              <li><a href="/pms/emerald-equity" className="text-neutral-100 hover:text-[#F49611] transition-colors">EMERALDE E</a></li>
            </ul>
          </div>
          {/* PMS */}
          <div>
            <h3 className="text-lg font-semibold mb-4">&nbsp;</h3>
            <ul className="space-y-3">
              <li><a href="/spark-pms-smallcap-5000-cr-cos" className="text-neutral-100 hover:text-[#F49611] transition-colors">SPARK</a></li>
              <li><a href="/pms/emerald-non-equity" className="text-neutral-100 hover:text-[#F49611] transition-colors">EMERALD NE</a></li>
            </ul>
          </div>

          {/* PMS */}
          <div>
            <h3 className="text-lg font-semibold mb-4">&nbsp;</h3>
            <ul className="space-y-3">
              <li><a href="/gem-pms-quality-momentum" className="text-neutral-100 hover:text-[#F49611] transition-colors">GEM</a></li>
              <li><a href="/pms/diamond" className="text-neutral-100 hover:text-[#F49611] transition-colors">DIAMOND</a></li>
            </ul>
          </div>
        </div>
        {/* 3rd Main Links Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Portfolio Management */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#F49611]">Financial Planning</h3>
            <ul className="space-y-3">
              <li><a href="/services/financial-planning-for-education-undergraduate-education-abroad" className="text-neutral-100 hover:text-[#F49611] transition-colors">UG Education Abroad</a></li>
              <li><a href="/services/financial-planning-for-education-undergraduate-education-in-india" className="text-neutral-100 hover:text-[#F49611] transition-colors">UG Education in India</a></li>
              <li><a href="/services/financial-planning-for-education-masters-education-abroad" className="text-neutral-100 hover:text-[#F49611] transition-colors">Masters Education Abroad</a></li>
              <li><a href="/services/financial-planning-in-case-of-sudden-death-of-income-earner" className="text-neutral-100 hover:text-[#F49611] transition-colors">Helping Child Overseas</a></li>
            </ul>
          </div>
          {/* PMS */}
          <div>
            <h3 className="text-lg font-semibold mb-4">&nbsp;</h3>
            <ul className="space-y-3">
              <li><a href="/services/customised-financial-planning-for-professionals-sports-persons" className="text-neutral-100 hover:text-[#F49611] transition-colors">Sports Persons</a></li>
              <li><a href="/services/customised-financial-planning-for-professionals-doctors" className="text-neutral-100 hover:text-[#F49611] transition-colors">Doctors</a></li>
              <li><a href="/services/customised-financial-planning-for-professionals-retired-bank-employees" className="text-neutral-100 hover:text-[#F49611] transition-colors">Retired Bank Employees</a></li>
              <li><a href="/services/customised-financial-planning-for-professionals-enterpreneurs" className="text-neutral-100 hover:text-[#F49611] transition-colors">Entrepreneurs</a></li>
            </ul>
          </div>

          {/* PMS */}
          <div>
            <h3 className="text-lg font-semibold mb-4">&nbsp;</h3>
            <ul className="space-y-3">
              <li><a href="/services/financial-planning-for-life-situations-child-birth" className="text-neutral-100 hover:text-[#F49611] transition-colors">Child Birth</a></li>
              <li><a href="/services/financial-planning-for-life-situations-childs-marriage" className="text-neutral-100 hover:text-[#F49611] transition-colors">Child Marriage</a></li>
              <li><a href="/services/financial-planning-for-life-situations-health-emergency" className="text-neutral-100 hover:text-[#F49611] transition-colors">Health Emergency</a></li>
              <li><a href="/services/financial-planning-for-life-situations-single-mothers" className="text-neutral-100 hover:text-[#F49611] transition-colors">Single Mothers</a></li>
              <li><a href="/services/financial-planning-for-single-parent" className="text-neutral-100 hover:text-[#F49611] transition-colors">Single Parents</a></li>
            </ul>
          </div>

          {/* PMS */}
          <div>
            <h3 className="text-lg font-semibold mb-4">&nbsp;</h3>
            <ul className="space-y-3">
              <li><a href="/services/financial-planning-for-life-situations-disability" className="text-neutral-100 hover:text-[#F49611] transition-colors">Disability</a></li>
              <li><a href="/services/financial-planning-in-case-of-sudden-death-of-income-earner" className="text-neutral-100 hover:text-[#F49611] transition-colors">Sudden Death of Income Earners</a></li>
              <li><a href="/services/financial-planning-for-life-situations-divorce" className="text-neutral-100 hover:text-[#F49611] transition-colors">Divorce</a></li>
              <li><a href="/services/financial-planning-for-life-situations-those-emigrating-from-india" className="text-neutral-100 hover:text-[#F49611] transition-colors">Migrating from india</a></li>
              <li><a href="/services/financial-planning-for-those-returning-to-india" className="text-neutral-100 hover:text-[#F49611] transition-colors">Returning to india</a></li>
            </ul>
          </div>
        </div>

        {/* 4th Main Links Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Portfolio Management */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#F49611]">Retirement Planning</h3>
            <ul className="space-y-3">
              <li><a href="/services/retirement-planning-services" className="text-neutral-100 hover:text-[#F49611] transition-colors">Traditional Retirement Planning</a></li>
              <li><a href="/services/post-retirement-solutions" className="text-neutral-100 hover:text-[#F49611] transition-colors">Post Retirement Solutions</a></li>
              <li><a href="/services/financial-independence-early-retirement-planning-fire" className="text-neutral-100 hover:text-[#F49611] transition-colors">FIRE Planning</a></li>
            </ul>
          </div>
          {/* PMS */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#F49611]">Goal Based Planning</h3>
            <ul className="space-y-3">
              <li><a href="https://club.maxiomwealth.com/goals/add?goal_type=CAR" className="text-neutral-100 hover:text-[#F49611] transition-colors">Car Purchase</a></li>
              <li><a href="https://club.maxiomwealth.com/goals/add?goal_type=HE" className="text-neutral-100 hover:text-[#F49611] transition-colors">Education Goal</a></li>
              <li><a href="https://club.maxiomwealth.com/goals/add?goal_type=HME" className="text-neutral-100 hover:text-[#F49611] transition-colors">Home Ownership</a></li>
            </ul>
          </div>

          {/* PMS */}
          <div>
            <h3 className="text-lg font-semibold mb-4">&nbsp;</h3>
            <ul className="space-y-3">
              <li><a href="https://club.maxiomwealth.com/goals/add?goal_type=CMR" className="text-neutral-100 hover:text-[#F49611] transition-colors">Marriage Goal</a></li>
              <li><a href="https://club.maxiomwealth.com/goals/add?goal_type=BK" className="text-neutral-100 hover:text-[#F49611] transition-colors">Bike Loan</a></li>
              <li><a href="https://club.maxiomwealth.com/Goals/BasicDetails?type=RETIREMENT&from" className="text-neutral-100 hover:text-[#F49611] transition-colors">Retirement Goal</a></li>
            </ul>
          </div>
        </div>

        {/* 5th Main Links Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Portfolio Management */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-[#F49611]">Financial Calculators</h3>
            <ul className="space-y-3">
              <li><a href="/resources/calculators/investment-calculators" className="text-neutral-100 hover:text-[#F49611] transition-colors">Investment Calculators</a></li>
              <li><a href="/resources/calculators/personal-finance-calculators" className="text-neutral-100 hover:text-[#F49611] transition-colors">Personal Finance Calculators</a></li>
              <li><a href="/resources/calculators/family-calculators" className="text-neutral-100 hover:text-[#F49611] transition-colors">Children & Family Calculators</a></li>
            </ul>
          </div>
          {/* PMS */}
          <div>
            <h3 className="text-lg font-semibold mb-4">&nbsp;</h3>
            <ul className="space-y-3">
              <li><a href="/resources/calculators/retirement-calculators" className="text-neutral-100 hover:text-[#F49611] transition-colors">Retirement Calculators</a></li>
              <li><a href="/resources/calculators/education-career-calculators" className="text-neutral-100 hover:text-[#F49611] transition-colors">Education and Career Calculators</a></li>
              <li><a href="/resources/calculators/goal-planning-calculators" className="text-neutral-100 hover:text-[#F49611] transition-colors">Lifestyle and Goal Planning Calculators</a></li>
            </ul>
          </div>
          {/* PMS */}
          <div>
            <h3 className="text-lg font-semibold mb-4">&nbsp;</h3>
            <ul className="space-y-3">
              <li><a href="/resources/calculators/tax-calculators" className="text-neutral-100 hover:text-[#F49611] transition-colors">Tax Calculators</a></li>
              <li><a href="/resources/calculators/income-cashflow-calculators" className="text-neutral-100 hover:text-[#F49611] transition-colors">Income & Cashflow Calculators</a></li>
              <li><a href="/resources/calculators/trading-calculators" className="text-neutral-100 hover:text-[#F49611] transition-colors">Trading Calculators</a></li>
            </ul>
          </div>

          {/* PMS */}
          <div>
            <h3 className="text-lg font-semibold mb-4">&nbsp;</h3>
            <ul className="space-y-3">
              <li><a href="/resources/calculators/loan-calculators" className="text-neutral-100 hover:text-[#F49611] transition-colors">Loan Calculators</a></li>
              <li><a href="/resources/calculators/miscellaneous-calculators" className="text-neutral-100 hover:text-[#F49611] transition-colors">Miscellaneous Calculators</a></li>
            </ul>
          </div>
        </div>
        {/* Social Links */}
        <div className="border-t border-primary-300/20 pt-8 mb-8">
          <div className="flex flex-wrap justify-between items-center gap-8">
            <div className="flex gap-6">
              <a href="https://www.facebook.com/maxiomwealth" target="_blank" rel="noopener noreferrer" className="text-neutral-100 hover:text-[#F49611] transition-colors">
                <Facebook className="w-6 h-6" />
              </a>
              <a href="https://x.com/maxiomwealth" target="_blank" rel="noopener noreferrer" className="text-neutral-100 hover:text-[#F49611] transition-colors">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"
                    fill="currentColor" />
                </svg>
              </a>
              <a href="https://www.linkedin.com/company/maxiomwealth/" target="_blank" rel="noopener noreferrer" className="text-neutral-100 hover:text-[#F49611] transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                    <path d="M20.5 2h-17A1.5 1.5 0 002 3.5v17A1.5 1.5 0 003.5 22h17a1.5 1.5 0 001.5-1.5v-17A1.5 1.5 0 0020.5 2zM8 19H5v-9h3zM6.5 8.25A1.75 1.75 0 118.3 6.5a1.78 1.78 0 01-1.8 1.75zM19 19h-3v-4.74c0-1.42-.6-1.93-1.38-1.93A1.74 1.74 0 0013 14.19a.66.66 0 000 .14V19h-3v-9h2.9v1.3a3.11 3.11 0 012.7-1.4c1.55 0 3.36.86 3.36 3.66z"></path>
                  </svg>
                </a>
              <a href="https://www.instagram.com/maxiomwealth/" target="_blank" rel="noopener noreferrer" className="text-neutral-100 hover:text-[#F49611] transition-colors">
                <Instagram className="w-6 h-6" />
              </a>
              <a href="https://maxiomwealth.quora.com/" target="_blank" rel="noopener noreferrer" className="text-neutral-100 hover:text-[#F49611] transition-colors">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                  <path
                    d="M14.4484 20.125C15.0078 20.7812 16.188 20.9747 16.788 21C19.7125 20.924 20.2151 18.3701 19.9305 17.0456C18.9678 19.5171 17.0848 18.2255 16.5882 17.4829C16.0915 16.7403 15.5043 15.6705 15.0078 14.9924C12.5374 11.7072 9.71584 13.0152 8.61385 14.0798C11.5052 13.4935 12.9076 16.7577 13.2474 18.4631C12.6494 18.6298 12.0219 18.7186 11.3749 18.7186C7.30185 18.7186 4 15.1999 4 10.8593C4 6.51873 7.30185 3 11.3749 3C15.4479 3 18.7498 6.51873 18.7498 10.8593C18.7498 12.6423 18.4926 13.8375 17.5537 15.1562C17.1683 14.5337 16.0739 13.05 15.4636 12.5938C15.8632 9.72936 15.0078 4.55165 11.3749 4.4981C7.24786 4.43726 6.89425 10.1255 7.23333 12.9772"
                    stroke="currentColor"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </a>
              <a href="https://www.youtube.com/@maxiomwealth" target="_blank" rel="noopener noreferrer" className="text-neutral-100 hover:text-[#F49611] transition-colors">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-6 h-6">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"
                    fill="currentColor" />
                </svg>
              </a>
            </div>
            <div className="flex gap-6">
              <a href="/privacy-policy" className="text-neutral-100 hover:text-[#F49611] transition-colors">Privacy Policy</a>
              <a href="/terms-conditions" className="text-neutral-100 hover:text-[#F49611] transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>

        {/* Regulatory Information */}
        <div className="text-sm text-neutral-300 space-y-4">
          {/* SEBI Registration Information */}
          <div className="p-5 bg-neutral-900/30 rounded-lg border border-neutral-800">
            <div className="flex flex-col md:flex-row gap-5 items-center md:items-start">
              {/* SEBI Logo
              <div className="shrink-0 bg-white p-3 rounded-md">
                <img
                  src="https://jamaappprod.s3.ap-south-1.amazonaws.com/jama_wealth_website/New-SEBI-Logo.jpg"
                  alt="SEBI Logo"
                  className="h-14 w-auto"
                />
              </div> */}

              {/* Registration Information */}
              <div className="flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border-l-4 border-[#F49611] pl-3">
                    <p className="font-semibold text-white text-base">SEBI Registered Portfolio Manager</p>
                    <p className="font- text-white text-base">Maxiom Asset Management Pvt. Ltd.</p>
                    <p className="mt-1">Registration No: <span className="text-[#F49611]">INP000007881</span></p>
                    <p>Non Individual</p>
                  </div>

                  <div className="border-l-4 border-[#F49611] pl-3">
                    <p className="font-semibold text-white text-base">SEBI Registered Investment Advisor</p>
                    <p className="font- text-white text-base">Simply Grow Technologies Pvt. Ltd.</p>
                    <p className="mt-1">Registration No: <span className="text-[#F49611]">INA200015583</span></p>
                    <p>Type: Corporate, Validity: Perpetual</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* <div className="p-4 bg-neutral-900/30 rounded-lg border border-neutral-800">
            <p className="mb-2 font-semibold text-white">Regulatory Information:</p>
            <p>MaxiomWealth is a SEBI registered Investment Advisor (Registration No: <span className="text-[#F49611]">INA000000000</span>). CIN: <span className="text-[#F49611]">U000000XX0000XXX000000</span></p>
          </div> */}
          <p className="text-xs text-white">Disclaimer: Equity & Mutual Fund investments are subject to market risks. Please read all scheme related documents carefully before investing. Past performance is not indicative of future returns.</p>
          <p className="text-center pt-4">&copy; {new Date().getFullYear()} MaxiomWealth. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;